import * as React from 'react'
import { AppBar, Box, Drawer, IconButton, Link, Toolbar } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Link as RouterLink, NavLink, useLocation } from 'react-router-dom'
import TvButton from './shared/TvButton'
import Logo from './shared/Logo'
import { COLORS, MAX_WIDTH } from '../constants'

export type page = {
    title: string,
    href: string
}

// "Contact" is covered by the Start a Project button, so it isn't repeated in the desktop nav.
export const pages: page[] = [
    { title: 'Services', href: '/services' },
    { title: 'Work', href: '/work' },
    { title: 'Mentorship', href: '/mentorship' },
    { title: 'Resume', href: '/resume' },
]

const navLinkSx = {
    color: COLORS.mutedOnDark,
    fontWeight: 500,
    fontSize: '0.98rem',
    textDecoration: 'none',
    '&:hover': { color: COLORS.white },
    '&.active': { color: COLORS.white, boxShadow: `inset 0 -2px 0 ${COLORS.accent}` },
}

function Header() {
    const [open, setOpen] = React.useState(false)
    const location = useLocation()

    React.useEffect(() => setOpen(false), [location.pathname])

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{ backgroundColor: 'rgba(11,13,16,0.94)', backdropFilter: 'saturate(180%) blur(10px)', borderBottom: `1px solid ${COLORS.blackBorder}` }}
        >
            <Toolbar sx={{ width: '100%', maxWidth: MAX_WIDTH, mx: 'auto', px: { xs: 2.5, sm: 4 }, minHeight: { xs: 64, md: 72 }, justifyContent: 'space-between' }}>
                <Link component={RouterLink} to="/" underline="none" aria-label="tviles.com home">
                    <Logo size="sm" />
                </Link>

                <Box component="nav" aria-label="Main" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
                    {pages.map((p) => (
                        <Link key={p.href} component={NavLink} to={p.href} sx={{ ...navLinkSx, py: 1 }}>
                            {p.title}
                        </Link>
                    ))}
                    <TvButton text="Start a Project" to="/contact" size="small" />
                </Box>

                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
                    {/* Keep a visible path to the contact page on phones */}
                    <TvButton text="Contact" to="/contact" size="small" />
                    <IconButton
                        aria-label="Open menu"
                        aria-expanded={open}
                        aria-controls="mobile-menu"
                        aria-haspopup="dialog"
                        onClick={() => setOpen(true)}
                        sx={{ color: COLORS.white, mr: -1, width: 48, height: 48 }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>
            </Toolbar>

            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    id: 'mobile-menu',
                    role: 'dialog',
                    'aria-modal': true,
                    'aria-label': 'Site menu',
                    sx: { width: 'min(320px, 86vw)', backgroundColor: COLORS.black, color: COLORS.white, p: 3 },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Logo size="sm" />
                    <IconButton aria-label="Close menu" onClick={() => setOpen(false)} sx={{ color: COLORS.white, mr: -1, width: 48, height: 48 }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box component="nav" aria-label="Mobile" sx={{ display: 'flex', flexDirection: 'column' }}>
                    {[{ title: 'Home', href: '/' }, ...pages, { title: 'Contact', href: '/contact' }].map((p) => (
                        <Link
                            key={p.href}
                            component={NavLink}
                            to={p.href}
                            end={p.href === '/'}
                            sx={{ ...navLinkSx, fontSize: '1.2rem', py: 1.5, borderBottom: `1px solid ${COLORS.blackBorder}`, '&.active': { color: COLORS.accent } }}
                        >
                            {p.title}
                        </Link>
                    ))}
                </Box>
                <Box sx={{ mt: 4 }}>
                    <TvButton text="Start a Project" to="/contact" icon={<ArrowForwardIcon />} fullWidth />
                </Box>
            </Drawer>
        </AppBar>
    )
}
export default Header
